import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { throttle } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';

import { GET_PAY_STATION_STATUS_RESOURCE } from 'src/features/monitoring/api/useGetPayStationStatus';
import SingleMonitoringPayStation from 'src/features/monitoring/components/SingleMonitoringPayStation';
import { parkingStatusMessages } from 'src/features/monitoring/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { useListPayStations } from 'src/features/payment/api/useListPayStations';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { WebSocketEvent, WebSocketEventMessages } from 'src/features/visitor/types';
import { queryClient } from 'src/lib/react-query';
import { SectionBlock, SectionBlockBody } from 'src/registry/components/blocks/SectionBlock';
import Tab from 'src/registry/components/navigation/Tab';
import TabRow from 'src/registry/components/navigation/TabRow';
import { Button } from 'src/registry/ui/Button';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

export default function MonitoringPayStations() {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const [isOpen, setIsOpen] = React.useState(true);

    const { subscribe, unsubscribe } = React.useContext(WSContext);
    const { data, isLoading } = useListPayStations({ parkingId });

    const handleWebSocket = React.useMemo(
        () =>
            throttle((event: WebSocketEvent) => {
                if (
                    event.message === WebSocketEventMessages.PAY_STATION_SCREEN_CHANGE ||
                    event.message === WebSocketEventMessages.PAY_STATION_OPERATION_CHANGE ||
                    event.message === WebSocketEventMessages.PAY_STATION_CASH_LEVEL_CHANGE ||
                    event.message === WebSocketEventMessages.PAY_STATION_STATUS_CHANGE ||
                    event.message === WebSocketEventMessages.NEW_PAY_STATION_EVENT ||
                    event.message === WebSocketEventMessages.NEW_PAY_STATION_TRANSACTION
                ) {
                    void queryClient.invalidateQueries({
                        queryKey: [GET_PAY_STATION_STATUS_RESOURCE, event.data.pay_station_id],
                    });
                }
            }, 500),
        [],
    );

    React.useEffect(() => {
        subscribe(WebSocketGroup.PAY_STATION, handleWebSocket);

        return () => unsubscribe(WebSocketGroup.PAY_STATION, handleWebSocket);
    }, [handleWebSocket, subscribe, unsubscribe]);

    return (
        <>
            <div className="flex items-center justify-between">
                <TabRow>
                    <Tab end to={`/${PATH.MONITORING}`}>
                        <FormattedMessage {...parkingStatusMessages.gateways} />
                    </Tab>
                    <Tab end to={`/${PATH.MONITORING}/pay-station`}>
                        <FormattedMessage {...parkingStatusMessages.payStations} />
                    </Tab>
                    <Tab end to={`/${PATH.MONITORING}/devices`}>
                        <FormattedMessage {...parkingStatusMessages.devices} />
                    </Tab>
                </TabRow>
                <div className="flex justify-end">
                    <Button variant="outline" onClick={() => setIsOpen((prevState) => !prevState)}>
                        <FontAwesomeIcon icon={isOpen ? ICON_MAP.COMPRESS : ICON_MAP.EXPAND} />
                        {formatMessage(isOpen ? parkingStatusMessages.collapseAll : parkingStatusMessages.expandAll)}
                    </Button>
                </div>
            </div>
            <SectionBlock>
                <SectionBlockBody className="grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {isLoading &&
                        Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton key={index} className="h-160 rounded-xl" />
                        ))}
                    {data?.results.map((payStation) => (
                        <SingleMonitoringPayStation key={payStation.id} payStation={payStation} isOpen={isOpen} />
                    ))}
                </SectionBlockBody>
            </SectionBlock>
        </>
    );
}
